import useCachedResources from '@/core/useCachedResources';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { decode, encode } from 'base-64';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import Navigation from './navigation';

import { CurrenciesContextProvider } from '@/currencies';
import { LayoutContextProvider } from './contexts/LayoutContext';

import Axios from 'axios';

import { BrandConfig as FGA } from '@/brand-fga';
import { BrandConfig as GBF } from '@/brand-gbf';
import { BrandConfig as UTGL } from '@/brand-utgl';
import { BrandProvider, ThemeProvider } from '@/core/theme';
import EarnApp from '@/features/earn';
import { I18nModule } from '@/i18n';
import Constants from 'expo-constants';
import { ActivityIndicator, Platform, View } from 'react-native';
import { useInit } from './core/useInit.hook';
import BillPaymentApp from './features/bill-payment';
import ExchangeSubAccountApp from './features/exchange-sub-account';
import { useStore } from './store/store';
import AccountManagementApp from './features/account-management';
import { UserContextProvider } from '@/user/user.context';
import CardAccountApp from '@/features/card-account';
import CreditCardActivateCardApp from '@/features/account-management/screens/card-activation';
import UnionPayApp from './features/bill-payment/screens/union-pay';
import CreditCardCancelCardApp from '@/features/account-management/screens/cancel-card';
import CreditCardSuspendUnsuspendApp from '@/features/account-management/screens/suspend-card';
import CountriesApp from '@/features/countries';
import CardApp from '@/features/card';
import DigitalInjectionApp from '@/features/account-management/screens/digital-deposit';
import AccountTransferApp from '@/features/account-management/screens/accounts-transfers';

Axios.defaults.baseURL = process.env.UTX_API_URL || Constants.manifest?.extra?.utxApiUrl; // use your own URL here or environment variable

export const ACCESS_BASE_URL = process.env.UTX_ACCESS_API_URL || Constants.manifest?.extra?.utxAccessApiUrl;

if (!global.btoa) {
  global.btoa = encode;
}

if (!global.atob) {
  global.atob = decode;
}

const blackList = [undefined, '', null];

const setAxios = (token: string) => {
  Axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

  // Set interceptor to remove undefined values request query params
  // XXX this can be removed after
  Axios.interceptors.request.use((config) => {
    if (config.params && typeof config.params === 'object') {
      Object.keys(config.params).forEach((key) => {
        if (blackList.includes(config.params[key])) {
          delete config.params[key];
        }
      });
    }
    return config;
  });
};

export default function App() {
  const queryClient = new QueryClient();
  const isLoadingComplete = useCachedResources();
  const store = useStore();

  useStore.subscribe(({ authToken }) => {
    setAxios(authToken);
  });

  const { params, isReady, url } = useInit();

  // Set the locale once at the beginning of your app.
  const i18n = I18nModule({ locale: params.locale });

  const modules = [
    EarnApp,
    ExchangeSubAccountApp,
    CardAccountApp,
    AccountManagementApp,
    BillPaymentApp,
    CreditCardActivateCardApp,
    UnionPayApp,
    CreditCardCancelCardApp,
    CreditCardSuspendUnsuspendApp,
    CountriesApp,
    CardApp,
    DigitalInjectionApp,
    AccountTransferApp
  ];

  modules
    .filter((m) => m.i18n)
    .forEach((m: any) => {
      for (const [lng, res] of Object.entries(m.i18n)) {
        let bundle = res as any;
        Object.keys(bundle).forEach((key) => {
          i18n.services.addResourceBundle(lng, key, bundle[key]);
        });
      }
    });
  if (!isLoadingComplete || !isReady) {
    return (
      <View style={{ paddingTop: 25 }}>
        <ActivityIndicator size={'large'} />
      </View>
    );
  }

  return (
    <>
      <BrandProvider
        currentBrand={store.brand}
        brands={{
          UTGL,
          GBF,
          FGA
        }}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <UserContextProvider>
              <CurrenciesContextProvider>
                <LayoutContextProvider>
                  <SafeAreaProvider>
                    <StatusBar style="dark" />
                    <Navigation apps={modules} initialUrl={url} />
                  </SafeAreaProvider>
                </LayoutContextProvider>
              </CurrenciesContextProvider>
            </UserContextProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </BrandProvider>
    </>
  );
}
